/**
 * MONSTER TOWN — TOUCH. ARCHITECT-OWNED, FROZEN. Additive only.
 *
 * ================== THE HARD RULE ==================
 * ALL hit-testing goes through `canvas.getBoundingClientRect()`.
 * NEVER window.innerWidth / window.innerHeight / screen.*.
 * Older iPads report a window height that does not match the canvas, and every
 * button ends up hit-testing one slot off. This exact bug shipped once already
 * in OCEAN RUSH. `canvasNDC()` below is the only sanctioned conversion.
 * ===================================================
 *
 * KID-USABILITY RULES BAKED IN HERE:
 *  - No double-tap gesture exists. There is no way to require one.
 *  - Tap tolerance is generous (18 CSS px) — a 3-year-old's finger slides.
 *  - Hold is 420 ms, not the OS default 700 ms, and it is always cancellable by
 *    lifting: nothing destructive happens on hold-down, only on hold-release.
 *  - Pinch needs no precision; it is clamped and smoothed by the camera.
 *  - Every pointer event is passive:false + preventDefault so iOS never
 *    rubber-band-scrolls the page under the canvas.
 */

export interface CanvasPoint {
  /** CSS pixels relative to the canvas top-left. */
  x: number
  y: number
  /** Normalised device coords for three.js raycasting, -1..1. */
  ndcX: number
  ndcY: number
}

export interface TouchGestureConfig {
  tapMaxMs: number
  tapMaxMovePx: number
  holdMs: number
  dragStartPx: number
}

export const DEFAULT_TOUCH_CONFIG: TouchGestureConfig = {
  tapMaxMs: 600,
  tapMaxMovePx: 18,
  holdMs: 420,
  dragStartPx: 14,
}

/** Minimum touch target, CSS px. The UI crew must not go below this. */
export const MIN_TOUCH_TARGET_PX = 88

/**
 * THE ONLY sanctioned client-coords -> canvas/NDC conversion in the codebase.
 * If you write `window.innerWidth` in a hit test, you have introduced the bug.
 */
export function canvasNDC(canvas: HTMLCanvasElement, clientX: number, clientY: number): CanvasPoint {
  const r = canvas.getBoundingClientRect()
  const x = clientX - r.left
  const y = clientY - r.top
  // Guard against a zero-size rect during rotation / first layout on iOS.
  const w = r.width > 0 ? r.width : 1
  const h = r.height > 0 ? r.height : 1
  return {
    x,
    y,
    ndcX: (x / w) * 2 - 1,
    ndcY: -(y / h) * 2 + 1,
  }
}

/** True if the point is inside the canvas rect. Uses the rect, obviously. */
export function insideCanvas(canvas: HTMLCanvasElement, clientX: number, clientY: number): boolean {
  const r = canvas.getBoundingClientRect()
  return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom
}

export interface DragEvent {
  start: CanvasPoint
  current: CanvasPoint
  dx: number
  dy: number
}

export interface PinchEvent {
  /** Ratio vs the pinch start distance. >1 = fingers spreading = zoom in. */
  scale: number
  /** Midpoint between the two fingers, in canvas space. */
  center: CanvasPoint
}

type Unsub = () => void

interface Active {
  id: number
  startX: number
  startY: number
  startT: number
  lastX: number
  lastY: number
  moved: boolean
  dragging: boolean
  holdFired: boolean
  holdTimer: ReturnType<typeof setTimeout> | null
}

/**
 * Pointer-event based gesture router. One instance per canvas.
 * Handlers are additive; every `on*` returns an unsubscribe function.
 */
export class TouchRouter {
  readonly canvas: HTMLCanvasElement
  readonly config: TouchGestureConfig
  /** True from the first touch/pen event onward — drives the pixel-ratio cap. */
  isTouchDevice = false

  private pointers = new Map<number, Active>()
  private pinchStartDist = 0
  private pinching = false
  private disposed = false

  private tapH: ((p: CanvasPoint) => void)[] = []
  private holdH: ((p: CanvasPoint) => void)[] = []
  private dragStartH: ((e: DragEvent) => void)[] = []
  private dragMoveH: ((e: DragEvent) => void)[] = []
  private dragEndH: ((e: DragEvent) => void)[] = []
  private pinchH: ((e: PinchEvent) => void)[] = []
  private hoverH: ((p: CanvasPoint) => void)[] = []

  constructor(canvas: HTMLCanvasElement, config: Partial<TouchGestureConfig> = {}) {
    this.canvas = canvas
    this.config = { ...DEFAULT_TOUCH_CONFIG, ...config }
    canvas.style.touchAction = 'none'
    canvas.addEventListener('pointerdown', this.onDown, { passive: false })
    canvas.addEventListener('pointermove', this.onMove, { passive: false })
    canvas.addEventListener('pointerup', this.onUp, { passive: false })
    canvas.addEventListener('pointercancel', this.onCancel, { passive: false })
    canvas.addEventListener('pointerleave', this.onCancel, { passive: false })
    canvas.addEventListener('contextmenu', this.onContextMenu)
  }

  onTap(fn: (p: CanvasPoint) => void): Unsub {
    this.tapH.push(fn)
    return () => this.drop(this.tapH, fn)
  }
  onHold(fn: (p: CanvasPoint) => void): Unsub {
    this.holdH.push(fn)
    return () => this.drop(this.holdH, fn)
  }
  onDragStart(fn: (e: DragEvent) => void): Unsub {
    this.dragStartH.push(fn)
    return () => this.drop(this.dragStartH, fn)
  }
  onDragMove(fn: (e: DragEvent) => void): Unsub {
    this.dragMoveH.push(fn)
    return () => this.drop(this.dragMoveH, fn)
  }
  onDragEnd(fn: (e: DragEvent) => void): Unsub {
    this.dragEndH.push(fn)
    return () => this.drop(this.dragEndH, fn)
  }
  onPinch(fn: (e: PinchEvent) => void): Unsub {
    this.pinchH.push(fn)
    return () => this.drop(this.pinchH, fn)
  }
  /** Mouse-only convenience for desktop ghost preview. Never fires on touch. */
  onHover(fn: (p: CanvasPoint) => void): Unsub {
    this.hoverH.push(fn)
    return () => this.drop(this.hoverH, fn)
  }

  private drop<T>(list: T[], fn: T): void {
    const i = list.indexOf(fn)
    if (i >= 0) list.splice(i, 1)
  }

  private emit<T>(list: ((v: T) => void)[], v: T): void {
    for (const fn of [...list]) {
      try {
        fn(v)
      } catch (err) {
        console.error('[touch] handler threw', err)
      }
    }
  }

  private point(e: PointerEvent): CanvasPoint {
    return canvasNDC(this.canvas, e.clientX, e.clientY)
  }

  private onContextMenu = (e: Event): void => {
    e.preventDefault()
  }

  private onDown = (e: PointerEvent): void => {
    if (this.disposed) return
    e.preventDefault()
    if (e.pointerType !== 'mouse') this.isTouchDevice = true
    this.canvas.setPointerCapture?.(e.pointerId)

    const a: Active = {
      id: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startT: performance.now(),
      lastX: e.clientX,
      lastY: e.clientY,
      moved: false,
      dragging: false,
      holdFired: false,
      holdTimer: null,
    }
    a.holdTimer = setTimeout(() => {
      if (!a.moved && this.pointers.has(a.id) && !this.pinching) {
        a.holdFired = true
        this.emit(this.holdH, canvasNDC(this.canvas, a.lastX, a.lastY))
      }
    }, this.config.holdMs)
    this.pointers.set(e.pointerId, a)

    if (this.pointers.size === 2) {
      this.pinching = true
      this.pinchStartDist = this.twoFingerDist()
      for (const p of this.pointers.values()) this.clearHold(p)
    }
  }

  private onMove = (e: PointerEvent): void => {
    if (this.disposed) return
    const a = this.pointers.get(e.pointerId)
    if (a === undefined) {
      if (e.pointerType === 'mouse') this.emit(this.hoverH, this.point(e))
      return
    }
    e.preventDefault()
    a.lastX = e.clientX
    a.lastY = e.clientY
    const dx = e.clientX - a.startX
    const dy = e.clientY - a.startY
    const dist = Math.hypot(dx, dy)
    if (dist > this.config.tapMaxMovePx) a.moved = true

    if (this.pinching && this.pointers.size >= 2) {
      const d = this.twoFingerDist()
      if (this.pinchStartDist > 0 && d > 0) {
        this.emit(this.pinchH, { scale: d / this.pinchStartDist, center: this.twoFingerCenter() })
      }
      return
    }

    if (!a.dragging && dist > this.config.dragStartPx) {
      a.dragging = true
      this.clearHold(a)
      this.emit(this.dragStartH, this.dragEvent(a))
    }
    if (a.dragging) this.emit(this.dragMoveH, this.dragEvent(a))
  }

  private onUp = (e: PointerEvent): void => {
    if (this.disposed) return
    const a = this.pointers.get(e.pointerId)
    if (a === undefined) return
    e.preventDefault()
    this.clearHold(a)
    this.pointers.delete(e.pointerId)
    this.canvas.releasePointerCapture?.(e.pointerId)

    if (this.pinching) {
      if (this.pointers.size < 2) {
        this.pinching = false
        this.pinchStartDist = 0
      }
      return
    }
    if (a.dragging) {
      this.emit(this.dragEndH, this.dragEvent(a))
      return
    }
    if (a.holdFired) return
    const heldMs = performance.now() - a.startT
    if (!a.moved && heldMs <= this.config.tapMaxMs) {
      this.emit(this.tapH, canvasNDC(this.canvas, a.lastX, a.lastY))
    }
  }

  private onCancel = (e: PointerEvent): void => {
    const a = this.pointers.get(e.pointerId)
    if (a === undefined) return
    this.clearHold(a)
    if (a.dragging) this.emit(this.dragEndH, this.dragEvent(a))
    this.pointers.delete(e.pointerId)
    if (this.pointers.size < 2) {
      this.pinching = false
      this.pinchStartDist = 0
    }
  }

  private clearHold(a: Active): void {
    if (a.holdTimer !== null) {
      clearTimeout(a.holdTimer)
      a.holdTimer = null
    }
  }

  private dragEvent(a: Active): DragEvent {
    return {
      start: canvasNDC(this.canvas, a.startX, a.startY),
      current: canvasNDC(this.canvas, a.lastX, a.lastY),
      dx: a.lastX - a.startX,
      dy: a.lastY - a.startY,
    }
  }

  private twoFingerDist(): number {
    const [a, b] = [...this.pointers.values()]
    if (a === undefined || b === undefined) return 0
    return Math.hypot(a.lastX - b.lastX, a.lastY - b.lastY)
  }

  private twoFingerCenter(): CanvasPoint {
    const [a, b] = [...this.pointers.values()]
    if (a === undefined || b === undefined) return canvasNDC(this.canvas, 0, 0)
    return canvasNDC(this.canvas, (a.lastX + b.lastX) / 2, (a.lastY + b.lastY) / 2)
  }

  /** Harness/testing hook: synthesise a tap at canvas CSS coordinates. */
  simulateTapAtCanvasPx(x: number, y: number): void {
    const r = this.canvas.getBoundingClientRect()
    this.emit(this.tapH, canvasNDC(this.canvas, r.left + x, r.top + y))
  }

  dispose(): void {
    this.disposed = true
    this.canvas.removeEventListener('pointerdown', this.onDown)
    this.canvas.removeEventListener('pointermove', this.onMove)
    this.canvas.removeEventListener('pointerup', this.onUp)
    this.canvas.removeEventListener('pointercancel', this.onCancel)
    this.canvas.removeEventListener('pointerleave', this.onCancel)
    this.canvas.removeEventListener('contextmenu', this.onContextMenu)
    for (const p of this.pointers.values()) this.clearHold(p)
    this.pointers.clear()
  }
}
